import React, { useMemo, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import Header from './header';
// import Footer from './footer';
import Footer from './footer';
import BottomFooter from './bottom-footer';
import DevTools from '../devtools';
import NotificationBar from '../ui/notification-bar';
import { HeaderRes, FooterRes, BottomFooterRes } from '../../typescript/response';
import { Page } from '../../typescript/pages';
import { UtilityNav } from './utility-nav';
import { PageContext, usePageContext, usePageState } from '../../context/pageContext';
import { BreadcrumbLabelsProvider } from '../../context/breadcrumbLabels';

const Layout = React.memo(function Layout({
  header,
  footer,
  bottomFooter,
  page,
  children,
}: {
  header: HeaderRes;
  footer: FooterRes;
  bottomFooter: BottomFooterRes;
  page: Page;
  children: ReactNode;
}) {
  const [layout, setLayout] = useState({ header, footer, bottomFooter });
  const headerRef = useRef<HTMLDivElement | null>(null);
  const utilityNavRef = useRef<HTMLDivElement | null>(null);

  const navHeaderList = useMemo(
    () => layout.header?.navigation_menu || [],
    [layout.header?.navigation_menu],
  );
  const navFooterList = useMemo(
    () => layout.footer?.navigation_menu || [],
    [layout.footer?.navigation_menu],
  );

  const jsonObj: Record<string, any> = useMemo(() => {
    const obj: Record<string, any> = {
      ...(layout.header && { header: layout.header }),
      ...(layout.footer && { footer: layout.footer }),
      ...(layout.bottomFooter && { bottomFooter: layout.bottomFooter }),
    };
    if (page) obj.page = page;
    return obj;
  }, [layout, page]);

  const updateLayout = useCallback(() => {
    if (header && footer) {
      setLayout({ header, footer, bottomFooter });
    }
  }, [header, footer, bottomFooter]);

  useEffect(() => {
    updateLayout();
  }, [updateLayout]);

  const footerLayout = footer?.footer_layout && footer?.footer_layout !== 'default' ? footer?.footer_layout : null;

  const GlobalCssVars = () => {
    const [isNavOpen] = usePageState('isNavOpen');
    const { getGlobalCssVars, setGlobalCssVar } = usePageContext();

    useEffect(() => {
      if (typeof window === 'undefined') return;

      setGlobalCssVar('--headerHeight', `${headerRef.current?.clientHeight}px`);
      setGlobalCssVar('--utilityNavHeight', `${utilityNavRef.current?.clientHeight}px`);
    }, [isNavOpen]);
    
    const cssVars = Object.entries(getGlobalCssVars())
    .map(([k, v]) => `${k}: ${v};`)
    .join('\n');

    return <style>{`:root {${cssVars}}`}</style>;
  }

  return (
    <PageContext>
      <div className="layout">
        <a className="skip-to-content" href="#maincontent">Skip to content</a>
        {layout.header && (
          <>
            <UtilityNav
              ref={utilityNavRef}
              contact_us={layout.header.contact_us}
              locations={layout.header.locations}
              languages={layout.header.languages}
              header_strings={layout.header.header_strings}
            >
              {layout.header.notification_bar && (
                <NotificationBar notification_bar={layout.header.notification_bar} />
              )}
            </UtilityNav>
            <Header
              ref={headerRef}
              header={layout.header}
              navMenu={navHeaderList}
            />
          </>
        )}

        <main
          id="maincontent"
          className="mainClass"
          tabIndex={-1}
        >
          <BreadcrumbLabelsProvider navMenu={navHeaderList}>
            {children}
          </BreadcrumbLabelsProvider>
          {process.env.NODE_ENV !== 'production' && Object.keys(jsonObj).length > 0 && (
            <DevTools response={jsonObj} />
          )}
        </main>

        {layout.footer && (
          <Footer
            data={layout.footer}
          />
        )}
        {/* BottomFooter only to support the old Footer layout */}
        {!footerLayout && layout.bottomFooter && <BottomFooter bottomFooter={layout.bottomFooter} />}
      </div>
      <GlobalCssVars />
    </PageContext>
  );
});

export default Layout;